import React, { useState, useEffect, useRef } from 'react';
import {
  Box, FormControl, FormLabel, Input, Select, Switch,
  VStack, HStack, Button, Textarea, NumberInput, NumberInputField,
  InputGroup, InputRightElement, Text, useToast, Image, SimpleGrid,
  Modal, ModalOverlay, ModalContent, ModalBody, ModalCloseButton, useDisclosure
} from '@chakra-ui/react';
import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api';
import { generateMultaPDF } from '../../../utils/pdfGenerator';
import { policiaMunicipalApi, MultaPolicia, ESTADOS_MEXICO, TIPOS_VEHICULO, LICENCIAS_FEDERALES, LICENCIAS_ESTATALES } from '../../../services/policiaMunicipalApi';
import { MultaPolicia as MultaPDF } from '../../../utils/pdfGenerator';
import EvidenciasFieldSimple from '../../../components/EvidenciasFieldSimple';
import { GOOGLE_MAPS_API_KEY } from 'config/googleMaps';

interface Props {
  modo: 'view' | 'edit' | 'new';
  data?: MultaPolicia;
  onSave?: () => void;
}

interface RegistroMultaRef {
  cleanupCamera: () => void;
}

const RegistroMultaPolicia = React.forwardRef<RegistroMultaRef, Props>(({ modo, data, onSave }, ref) => {
  const toast = useToast();
  const evidenciasRef = useRef<{ cleanup: () => void }>(null);
  const [identificacionFiles, setIdentificacionFiles] = useState<FileList | null>(null);
  const [evidenciasFiles, setEvidenciasFiles] = useState<FileList | null>(null);
  const [ordenGruaFiles, setOrdenGruaFiles] = useState<FileList | null>(null);
  const [showOrdenGruaUpload, setShowOrdenGruaUpload] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [existingIdentificacion, setExistingIdentificacion] = useState<any[]>([]);
  const [existingEvidencias, setExistingEvidencias] = useState<any[]>([]);
  const [selectedImage, setSelectedImage] = useState<string>('');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [formData, setFormData] = useState<MultaPolicia>({
    folio: '',
    fechaInfraccion: new Date().toISOString().split('T')[0],
    horaInfraccion: new Date().toTimeString().split(' ')[0].substring(0, 5),
    lugarInfraccion: 'San Juan Totolac, Tlax., México',
    latitud: 19.3158,
    longitud: -98.1667,
    numeroPlaca: '',
    estadoEmisorPlaca: 'Tlaxcala',
    vigenciaPlaca: '',
    tipoVehiculo: '',
    categoriaVehiculo: '',
    tipoVehiculoCategoria: 'Particular',
    marca: '',
    modelo: '',
    color: '',
    nombreConductor: '',
    apellidosConductor: '',
    domicilioConductor: '',
    telefonoConductor: '',
    esPropietario: true,
    nombrePropietario: '',
    apellidosPropietario: '',
    domicilioPropietario: '',
    telefonoPropietario: '',
    articuloViolado: '',
    fraccion: '',
    descripcionConducta: '',
    tipoGarantia: 'licencia',
    tipoLicencia: 'estatal',
    categoriaLicencia: 'C',
    entidadEmisoraLicencia: 'Tlaxcala',
    placaGarantia: '',
    usarMismaPlaca: false,
    // Campos de aseguramiento
    numeroSerieVIN: '',
    numeroMotor: '',
    corralónAsignado: '',
    ubicacionAseguramiento: '',
    latitudAseguramiento: 19.3158,
    longitudAseguramiento: -98.1667,
    fechaAseguramiento: new Date().toISOString().split('T')[0],
    horaAseguramiento: new Date().toTimeString().split(' ')[0].substring(0, 5),
    observacionesAseguramiento: '',
    nombreOficial: '',
    gradoOficial: 'Agente',
    monto: 0,
    estatus: 'PENDIENTE',
    calificado: false
  });

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: ['places'],
  });

  // Limpiar cámara al desmontar el componente
  useEffect(() => {
    return () => {
      evidenciasRef.current?.cleanup();
    };
  }, []);

  // Exponer función de limpieza al componente padre
  React.useImperativeHandle(ref, () => ({
    cleanupCamera: () => {
      evidenciasRef.current?.cleanup();
    }
  }));

  useEffect(() => {
    if (data) {
      // Mapear datos del servidor al formato del frontend
      const mappedData = {
        ...data,
        fechaInfraccion: data.fechaInfraccion ? new Date(data.fechaInfraccion).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        horaInfraccion: data.horaInfraccion || new Date().toTimeString().split(' ')[0].substring(0, 5),
        latitud: data.latitud ? Number(data.latitud) : 19.3158,
        longitud: data.longitud ? Number(data.longitud) : -98.1667,
        estadoEmisorPlaca: (data as any).entidad || data.estadoEmisorPlaca,
        modelo: (data as any).modeloVehiculo || data.modelo,
        descripcionConducta: (data as any).descripcion || data.descripcionConducta,
        corralónAsignado: (data as any).corralon || data.corralónAsignado,
        tipoVehiculoCategoria: (data as any).tipoVehiculo === 'Particular' || (data as any).tipoVehiculo === 'Público' || (data as any).tipoVehiculo === 'SPF' ? (data as any).tipoVehiculo : 'Particular',
        tipoVehiculo: (data as any).carroceria?.descripcion || data.tipoVehiculo,
        categoriaVehiculo: (data as any).carroceria?.categoria || data.categoriaVehiculo || '',
        // Separar ciudadano en nombre y apellidos
        nombreConductor: (data as any).ciudadano ? (data as any).ciudadano.split(' ')[0] : data.nombreConductor,
        apellidosConductor: (data as any).ciudadano ? (data as any).ciudadano.split(' ').slice(1).join(' ') : data.apellidosConductor,
        monto: Number(data.monto) || 0
      };
      
      setFormData(mappedData);
      
      // Cargar archivos existentes
      if (data.id) {
        loadExistingFiles(data.id);
      }
    }
  }, [data]);

  const loadExistingFiles = async (multaId: number) => {
    try {
      const [identificacionRes, evidenciasRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/multas-transito/${multaId}/identificacion`),
        fetch(`${API_BASE_URL}/api/multas-transito/${multaId}/evidencias`)
      ]);
      
      if (identificacionRes.ok) {
        const identificacionData = await identificacionRes.json();
        console.log('Datos de identificación:', identificacionData);
        setExistingIdentificacion(identificacionData);
      }
      
      if (evidenciasRes.ok) {
        const evidenciasData = await evidenciasRes.json();
        console.log('Datos de evidencias:', evidenciasData);
        setExistingEvidencias(evidenciasData);
      }
    } catch (error) {
      console.log('Error cargando archivos:', error);
    }
  };

  useEffect(() => {
    if (!data && isLoaded) {
      // Geocodificar la ubicación por defecto al cargar
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: 'San Juan Totolac, Tlax., México' }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const loc = results[0].geometry.location;
          setFormData(prev => ({
            ...prev,
            latitud: loc.lat(),
            longitud: loc.lng()
          }));
        }
      });
    }
  }, [data, isLoaded]);

  const handleInputChange = (field: keyof MultaPolicia, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const buscarDireccion = () => {
    if (!formData.lugarInfraccion) return;
    
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: formData.lugarInfraccion }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const loc = results[0].geometry.location;
        setFormData(prev => ({
          ...prev,
          latitud: loc.lat(),
          longitud: loc.lng(),
          lugarInfraccion: results[0].formatted_address
        }));
      } else {
        toast({
          title: 'Dirección no encontrada',
          description: 'Especifica una dirección más precisa.',
          status: 'error',
        });
      }
    });
  };

  const obtenerUbicacionActual = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === 'OK' && results[0]) {
              setFormData(prev => ({
                ...prev,
                latitud: lat,
                longitud: lng,
                lugarInfraccion: results[0].formatted_address
              }));
            } else {
              setFormData(prev => ({ ...prev, latitud: lat, longitud: lng }));
            }
          });
        },
        (error) => {
          toast({
            title: 'Error de geolocalización',
            description: 'No se pudo obtener la ubicación actual.',
            status: 'error',
          });
        }
      );
    } else {
      toast({
        title: 'Geolocalización no disponible',
        description: 'Tu navegador no soporta geolocalización.',
        status: 'error',
      });
    }
  };

  const validateForm = () => {
    if (!formData.lugarInfraccion?.trim()) {
      toast({
        title: 'Error de validación',
        description: 'El lugar de infracción es requerido',
        status: 'error',
        duration: 3000,
      });
      return false;
    }
    
    if (!formData.numeroPlaca?.trim()) {
      toast({
        title: 'Error de validación',
        description: 'El número de placa es requerido',
        status: 'error',
        duration: 3000,
      });
      return false;
    }
    
    if (!formData.estadoEmisorPlaca?.trim()) {
      toast({
        title: 'Error de validación',
        description: 'La entidad emisora es requerida',
        status: 'error',
        duration: 3000,
      });
      return false;
    }
    
    if (formData.monto < 0) {
      toast({
        title: 'Error de validación',
        description: 'El monto no puede ser negativo',
        status: 'error',
        duration: 3000,
      });
      return false;
    }
    
    return true;
  };

  // Configuración del servidor - cambiar según el entorno
  const API_BASE_URL = `${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}`;

  const openImageModal = (imageUrl: string) => {
    setSelectedImage(imageUrl);
    onOpen();
  };

  const uploadFiles = async (multaId: number) => {
    setUploadingFiles(true);
    
    try {
      // Validar que el multaId sea válido
      if (!multaId || multaId <= 0) {
        throw new Error('ID de multa inválido para subir archivos');
      }
      
      // Verificar que la multa existe antes de subir archivos
      try {
        const checkMulta = await fetch(`${API_BASE_URL}/api/multas-transito/${multaId}`, {
          method: 'GET',
          signal: AbortSignal.timeout(5000)
        });
        if (!checkMulta.ok) {
          throw new Error(`Multa ${multaId} no encontrada`);
        }
      } catch (checkError) {
        throw new Error(`No se puede verificar la multa: ${checkError}`);
      }

      // Subir archivos de identificación
      if (identificacionFiles && identificacionFiles.length > 0) {
        if (identificacionFiles.length > 2) {
          throw new Error('Máximo 2 archivos de identificación permitidos');
        }
        const formDataIdentificacion = new FormData();
        Array.from(identificacionFiles).forEach((file, index) => {
          if (file.size > 10 * 1024 * 1024) {
            throw new Error(`El archivo ${file.name} es demasiado grande (máximo 10MB)`);
          }
          formDataIdentificacion.append('identificacion', file);
        });
        
        const responseIdentificacion = await fetch(`${API_BASE_URL}/api/multas-transito/${multaId}/identificacion`, {
          method: 'POST',
          body: formDataIdentificacion
        });
        
        if (!responseIdentificacion.ok) {
          const errorText = await responseIdentificacion.text();
          throw new Error(`Error al subir archivos de identificación: ${responseIdentificacion.status} - ${errorText}`);
        }
        
        const resultIdentificacion = await responseIdentificacion.json();
        

        
        // Limpiar archivos de identificación después de subirlos
        setIdentificacionFiles(null);
      }
      
      // Subir archivos de evidencias
      if (evidenciasFiles && evidenciasFiles.length > 0) {
        const formDataEvidencias = new FormData();
        Array.from(evidenciasFiles).forEach((file, index) => {
          if (file.size > 50 * 1024 * 1024) {
            throw new Error(`El archivo ${file.name} es demasiado grande (máximo 50MB)`);
          }
          formDataEvidencias.append('evidencias', file);
        });
        
        const responseEvidencias = await fetch(`${API_BASE_URL}/api/multas-transito/${multaId}/evidencias`, {
          method: 'POST',
          body: formDataEvidencias
        });
        
        if (!responseEvidencias.ok) {
          const errorText = await responseEvidencias.text();
          throw new Error(`Error al subir archivos de evidencias: ${responseEvidencias.status} - ${errorText}`);
        }
        
        const resultEvidencias = await responseEvidencias.json();
        

        
        // Limpiar archivos de evidencias después de subirlos
        setEvidenciasFiles(null);
      }
    } catch (error) {
      
      let errorMessage = 'Error desconocido al subir archivos';
      
      if (error instanceof Error) {
        if (error.message.includes('Failed to fetch')) {
          errorMessage = `Error de conexión: Verifica que el servidor esté ejecutándose en ${API_BASE_URL}`;
        } else if (error.message.includes('NetworkError')) {
          errorMessage = 'Error de red: Verifica tu conexión a internet';
        } else {
          errorMessage = error.message;
        }
      }
      
      toast({
        title: 'Error al subir archivos',
        description: errorMessage,
        status: 'error',
        duration: 8000,
      });
      throw error;
    } finally {
      setUploadingFiles(false);
    }
  };

  const handleRegistrar = async () => {
    if (!validateForm()) return;
    
    try {
      let multaCreada;
      let multaGuardada = false;
      
      if (modo === 'new') {
        multaCreada = await policiaMunicipalApi.createMulta(formData);
        multaGuardada = true;
        
        // Mostrar mensaje inicial
        const hasFiles = identificacionFiles || evidenciasFiles;
        
        // Subir archivos si existen
        if (multaCreada.id && hasFiles) {
          toast({
            title: 'Multa creada - Subiendo archivos',
            description: 'Por favor espere mientras se suben los archivos...',
            status: 'info',
            duration: 2000,
          });
          
          try {
            await uploadFiles(multaCreada.id);
            toast({
              title: 'Infracción registrada ✓',
              description: 'La infracción se ha guardado correctamente en los registros y archivos subidos',
              status: 'success',
              duration: 6000,
            });
          } catch (uploadError) {
            toast({
              title: 'Infracción registrada ✓',
              description: 'La infracción se ha guardado correctamente pero falló la subida de archivos',
              status: 'warning',
              duration: 8000,
            });
          }
        } else {
          toast({
            title: 'Infracción registrada ✓',
            description: 'La infracción se ha guardado correctamente en los registros',
            status: 'success',
            duration: 6000,
          });
        }
        
        // Mostrar sección de orden de grúa si es vehículo como garantía
        if (formData.tipoGarantia === 'vehiculo') {
          setShowOrdenGruaUpload(true);
          setTimeout(() => {
            toast({
              title: 'Vehículo asegurado',
              description: 'Ahora puedes adjuntar la orden de grúa en la sección habilitada',
              status: 'info',
              duration: 8000,
            });
          }, 1000);
        }
      } else if (modo === 'edit' && formData.id) {
               
        try {
          await policiaMunicipalApi.updateMulta(formData.id, formData);
          multaGuardada = true;
          
          // Mostrar mensaje inicial
          const hasFiles = identificacionFiles || evidenciasFiles;
          
          // Subir archivos si existen
          if (hasFiles) {
            toast({
              title: 'Infracción editada - Subiendo archivos',
              description: 'Por favor espere mientras se suben los archivos...',
              status: 'info',
              duration: 2000,
            });
            
            try {
              await uploadFiles(formData.id);
              toast({
                title: 'Éxito completo',
                description: 'Infracción editada y archivos subidos correctamente',
                status: 'success',
                duration: 6000,
              });
            } catch (uploadError) {
              
              toast({
                title: 'Infracción editada',
                description: 'Infracción guardada pero falló la subida de archivos',
                status: 'warning',
                duration: 8000,
              });
            }
          } else {
            toast({
              title: 'Éxito',
              description: 'Infracción actualizada correctamente',
              status: 'success',
              duration: 6000,
            });
          }
        } catch (editError) {
          
          throw editError;
        }
      } else if (modo === 'edit' && !formData.id) {
        throw new Error('No se puede editar: ID de infracción no encontrado');
      }
      
      if (multaGuardada) {
        evidenciasRef.current?.cleanup();
        onSave?.();
      }
    } catch (error) {
      console.error('Error completo:', error);
      
      let errorMessage = 'Error desconocido';
      
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error && typeof error === 'object') {
        errorMessage = JSON.stringify(error);
      }
      
      toast({
        title: modo === 'edit' ? 'Error al editar infracción' : 'Error al crear infracción',
        description: errorMessage,
        status: 'error',
        duration: 10000,
      });
    }
  };

  const handleImprimir = async () => {
    if (!validateForm()) return;
    
    try {
      // Convertir formData al formato esperado por el PDF
      const multaPDF: any = {
        id: formData.id,
        folio: formData.folio,
        fechaInfraccion: formData.fechaInfraccion,
        lugarInfraccion: formData.lugarInfraccion,
        latitud: formData.latitud,
        longitud: formData.longitud,
        numeroPlaca: formData.numeroPlaca,
        estadoEmisorPlaca: formData.estadoEmisorPlaca,
        vigenciaPlaca: formData.vigenciaPlaca,
        tipoVehiculo: formData.tipoVehiculo,
        marca: formData.marca,
        modelo: formData.modelo,
        color: formData.color,
        nombreConductor: formData.nombreConductor,
        apellidosConductor: formData.apellidosConductor,
        domicilioConductor: formData.domicilioConductor,
        telefonoConductor: formData.telefonoConductor,
        esPropietario: formData.esPropietario,
        nombrePropietario: formData.nombrePropietario,
        apellidosPropietario: formData.apellidosPropietario,
        domicilioPropietario: formData.domicilioPropietario,
        telefonoPropietario: formData.telefonoPropietario,
        articuloViolado: formData.articuloViolado,
        fraccion: formData.fraccion,
        descripcionConducta: formData.descripcionConducta,
        tipoGarantia: formData.tipoGarantia,
        tipoLicencia: formData.tipoLicencia,
        categoriaLicencia: formData.categoriaLicencia,
        entidadEmisoraLicencia: formData.entidadEmisoraLicencia,
        placaGarantia: formData.placaGarantia,
        usarMismaPlaca: formData.usarMismaPlaca,
        numeroSerieVIN: formData.numeroSerieVIN,
        numeroMotor: formData.numeroMotor,
        corralónAsignado: formData.corralónAsignado,
        ubicacionAseguramiento: formData.ubicacionAseguramiento,
        latitudAseguramiento: formData.latitudAseguramiento,
        longitudAseguramiento: formData.longitudAseguramiento,
        fechaAseguramiento: formData.fechaAseguramiento,
        horaAseguramiento: formData.horaAseguramiento,
        observacionesAseguramiento: formData.observacionesAseguramiento,
        nombreOficial: formData.nombreOficial,
        gradoOficial: formData.gradoOficial,
        monto: Number(formData.monto) || 0,
        estatus: formData.estatus,
        estatusCalificacion: formData.estatusCalificacion,
        creadaEn: formData.creadaEn,
        actualizadaEn: formData.actualizadaEn
      };
      await generateMultaPDF(multaPDF);
      toast({
        title: 'Éxito',
        description: 'Infracción impresa correctamente',
        status: 'success',
        duration: 6000,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: `No se pudo imprimir la infracción: ${error instanceof Error ? error.message : 'Error desconocido'}`,
        status: 'error',
        duration: 8000,
      });
    }
  };



  const isReadOnly = modo === 'view';

  if (!isLoaded) return <Text>Cargando mapa...</Text>;

  return (
    <Box p={3}>
      <VStack spacing={1} align="stretch">
        <Text fontWeight="bold" fontSize="lg" color="#5b182b" mb={4}>1. Identificación de la infracción</Text>
        
        <HStack spacing={4}>
          {formData.folio && (
            <FormControl>
              <FormLabel>Folio</FormLabel>
              <Input
                value={formData.folio}
                isReadOnly={true}
                bg="gray.100"
              />
            </FormControl>
          )}
          <FormControl isRequired>
            <FormLabel>Fecha de los hechos</FormLabel>
            <Input
              type="date"
              value={formData.fechaInfraccion}
              onChange={(e) => handleInputChange('fechaInfraccion', e.target.value)}
              isReadOnly={isReadOnly}
            />
          </FormControl>
          <FormControl isRequired>
            <FormLabel>Hora de la infracción</FormLabel>
            <Input
              type="time"
              value={formData.horaInfraccion}
              onChange={(e) => handleInputChange('horaInfraccion', e.target.value)}
              isReadOnly={isReadOnly}
            />
          </FormControl>
        </HStack>

        <Text fontWeight="bold" fontSize="lg" color="#5b182b" mb={4} mt={6}>2. Datos de la infracción</Text>
        
        <HStack spacing={4}>
          <FormControl isRequired>
            <FormLabel>Placa</FormLabel>
            <Input
              value={formData.numeroPlaca}
              onChange={(e) => handleInputChange('numeroPlaca', e.target.value)}
              isReadOnly={isReadOnly}
              placeholder="ABC-123-D"
            />
          </FormControl>
          <FormControl isRequired>
            <FormLabel>Estado emisor de la placa</FormLabel>
            <Select
              value={formData.estadoEmisorPlaca}
              onChange={(e) => handleInputChange('estadoEmisorPlaca', e.target.value)}
              disabled={isReadOnly}
            >
              {ESTADOS_MEXICO.map(estado => (
                <option key={estado} value={estado}>{estado}</option>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel>Vigencia</FormLabel>
            <Input
              value={formData.vigenciaPlaca}
              onChange={(e) => handleInputChange('vigenciaPlaca', e.target.value)}
              isReadOnly={isReadOnly}
              placeholder="Texto libre"
            />
          </FormControl>
        </HStack>

        <HStack spacing={4}>
          {(modo === 'new' || modo === 'edit') && (
            <FormControl isRequired>
              <FormLabel>Categoría de Auto</FormLabel>
              <Select
                value={formData.categoriaVehiculo || ''}
                onChange={(e) => {
                  handleInputChange('categoriaVehiculo', e.target.value);
                  // Limpiar la carrocería cuando cambie la categoría
                  handleInputChange('tipoVehiculo', '');
                }}
                disabled={isReadOnly}
              >
                <option value="">Seleccione categoría...</option>
                <option value="Automovil">Automóvil</option>
                <option value="Motocicleta">Motocicleta</option>
                <option value="Camion">Camión</option>
                <option value="Autobus">Autobús</option>
                <option value="Otro">Otro</option>
              </Select>
            </FormControl>
          )}
          
          <FormControl isRequired>
            <FormLabel>Carrocería</FormLabel>
            {(modo === 'new' || modo === 'edit') ? (
              <Select
                value={formData.tipoVehiculo}
                onChange={(e) => handleInputChange('tipoVehiculo', e.target.value)}
                disabled={isReadOnly || !formData.categoriaVehiculo}
              >
                <option value="">Seleccione carrocería...</option>
                {formData.categoriaVehiculo === 'Automovil' && [
                  <option key="Sedán" value="Sedán">Sedán</option>,
                  <option key="Hatchback" value="Hatchback">Hatchback</option>,
                  <option key="SUV" value="SUV">SUV</option>,
                  <option key="Coupé" value="Coupé">Coupé</option>,
                  <option key="Convertible" value="Convertible">Convertible</option>,
                  <option key="Pickup" value="Pickup">Pickup / Camioneta</option>,
                  <option key="Minivan" value="Minivan">Minivan / Van</option>
                ]}
                {formData.categoriaVehiculo === 'Motocicleta' && [
                  <option key="Motocicleta" value="Motocicleta">Motocicleta</option>,
                  <option key="Ciclomotor" value="Ciclomotor">Ciclomotor</option>,
                  <option key="Motocross" value="Motocross">Motocross / Enduro</option>
                ]}
                {formData.categoriaVehiculo === 'Camion' && [
                  <option key="Camión" value="Camión">Camión</option>,
                  <option key="Camioneta de carga" value="Camioneta de carga">Camioneta de carga</option>,
                  <option key="Tractocamión" value="Tractocamión">Tractocamión</option>,
                  <option key="Tráiler" value="Tráiler">Tráiler</option>
                ]}
                {formData.categoriaVehiculo === 'Autobus' && [
                  <option key="Microbús" value="Microbús">Microbús</option>,
                  <option key="Autobús" value="Autobús">Autobús</option>
                ]}
                {formData.categoriaVehiculo === 'Otro' && [
                  <option key="Otro" value="Otro">Otro (especificar)</option>
                ]}
              </Select>
            ) : (
              <Input
                value={formData.tipoVehiculo}
                isReadOnly={true}
                bg="gray.100"
              />
            )}
          </FormControl>
          <FormControl isRequired>
            <FormLabel>Tipo de vehículo</FormLabel>
            <Select
              value={formData.tipoVehiculoCategoria || 'Particular'}
              onChange={(e) => handleInputChange('tipoVehiculoCategoria', e.target.value)}
              disabled={isReadOnly}
            >
              <option value="Particular">Particular</option>
              <option value="Público">Público</option>
              <option value="SPF">SPF</option>
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel>Marca </FormLabel>
            <Input
              value={formData.marca}
              onChange={(e) => handleInputChange('marca', e.target.value)}
              isReadOnly={isReadOnly}
              placeholder="Toyota, Ford, etc."
            />
          </FormControl>
          <FormControl>
            <FormLabel>Modelo </FormLabel>
            <Input
              value={formData.modelo}
              onChange={(e) => handleInputChange('modelo', e.target.value)}
              isReadOnly={isReadOnly}
              placeholder="Corolla 2020"
            />
          </FormControl>
          <FormControl>
            <FormLabel>Color </FormLabel>
            <Input
              value={formData.color}
              onChange={(e) => handleInputChange('color', e.target.value)}
              isReadOnly={isReadOnly}
              placeholder="Blanco, Negro, etc."
            />
          </FormControl>
        </HStack>

        <Text fontWeight="bold" fontSize="lg" color="#5b182b" mb={4} mt={6}>3. Datos del conductor</Text>
        
        <HStack spacing={4}>
          <FormControl>
            <FormLabel>Nombre </FormLabel>
            <Input
              value={formData.nombreConductor}
              onChange={(e) => handleInputChange('nombreConductor', e.target.value)}
              isReadOnly={isReadOnly}
              placeholder="Nombre del conductor"
            />
          </FormControl>
          <FormControl>
            <FormLabel>Apellidos </FormLabel>
            <Input
              value={formData.apellidosConductor}
              onChange={(e) => handleInputChange('apellidosConductor', e.target.value)}
              isReadOnly={isReadOnly}
              placeholder="Apellidos del conductor"
            />
          </FormControl>
        </HStack>
        
        <HStack spacing={4}>
          <FormControl>
            <FormLabel>Domicilio </FormLabel>
            <Input
              value={formData.domicilioConductor}
              onChange={(e) => handleInputChange('domicilioConductor', e.target.value)}
              isReadOnly={isReadOnly}
              placeholder="Dirección completa"
            />
          </FormControl>
          <FormControl>
            <FormLabel>Teléfono de contacto </FormLabel>
            <Input
              value={formData.telefonoConductor}
              onChange={(e) => handleInputChange('telefonoConductor', e.target.value)}
              isReadOnly={isReadOnly}
              placeholder="Teléfono"
            />
          </FormControl>
        </HStack>
        
        <FormControl>
          <HStack>
            <Switch
              isChecked={formData.esPropietario}
              onChange={(e) => handleInputChange('esPropietario', e.target.checked)}
              isDisabled={isReadOnly}
            />
            <FormLabel mb={0}>El conductor es también el propietario</FormLabel>
          </HStack>
        </FormControl>
        
        {!formData.esPropietario && (
          <VStack spacing={3} align="stretch" p={4} bg="blue.50" borderRadius="md">
            <Text fontWeight="bold" color="#5b182b">Datos del propietario del vehículo</Text>
            <HStack spacing={4}>
              <FormControl>
                <FormLabel>Nombre</FormLabel>
                <Input
                  value={formData.nombrePropietario}
                  onChange={(e) => handleInputChange('nombrePropietario', e.target.value)}
                  isReadOnly={isReadOnly}
                  placeholder="Nombre del propietario"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Apellidos</FormLabel>
                <Input
                  value={formData.apellidosPropietario}
                  onChange={(e) => handleInputChange('apellidosPropietario', e.target.value)}
                  isReadOnly={isReadOnly}
                  placeholder="Apellidos del propietario"
                />
              </FormControl>
            </HStack>
            <HStack spacing={4}>
              <FormControl>
                <FormLabel>Domicilio</FormLabel>
                <Input
                  value={formData.domicilioPropietario}
                  onChange={(e) => handleInputChange('domicilioPropietario', e.target.value)}
                  isReadOnly={isReadOnly}
                  placeholder="Dirección completa"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Teléfono</FormLabel>
                <Input
                  value={formData.telefonoPropietario}
                  onChange={(e) => handleInputChange('telefonoPropietario', e.target.value)}
                  isReadOnly={isReadOnly}
                  placeholder="Teléfono"
                />
              </FormControl>
            </HStack>
          </VStack>
        )}

        <Text fontWeight="bold" fontSize="lg" color="#5b182b" mb={4} mt={6}>4. Infracción</Text>
        
        <HStack spacing={4}>
          <FormControl>
            <FormLabel>Artículo violentado</FormLabel>
            <Input
              value={formData.articuloViolado}
              onChange={(e) => handleInputChange('articuloViolado', e.target.value)}
              isReadOnly={isReadOnly}
              placeholder="Cuadro de texto"
            />
          </FormControl>
          <FormControl>
            <FormLabel>Fracción</FormLabel>
            <Input
              value={formData.fraccion}
              onChange={(e) => handleInputChange('fraccion', e.target.value)}
              isReadOnly={isReadOnly}
              placeholder="Cuadro de texto"
            />
          </FormControl>
        </HStack>
        
        <FormControl>
          <FormLabel>Descripción de la conducta</FormLabel>
          <Textarea
            value={formData.descripcionConducta}
            onChange={(e) => handleInputChange('descripcionConducta', e.target.value)}
            isReadOnly={isReadOnly}
            rows={3}
            placeholder="Sin límite de caracteres"
          />
        </FormControl>

        <Text fontWeight="bold" fontSize="lg" color="#5b182b" mb={4} mt={6}>5. Garantía</Text>
        
        <FormControl>
          <FormLabel>Tipo de garantía</FormLabel>
          <Select
            value={formData.tipoGarantia}
            onChange={(e) => handleInputChange('tipoGarantia', e.target.value)}
            disabled={isReadOnly}
          >
            <option value="licencia">Licencia de conducir</option>
            <option value="placa">Placa</option>
            <option value="vehiculo">Vehículo</option>
          </Select>
        </FormControl>
        
        {formData.tipoGarantia === 'licencia' && (
          <VStack spacing={3} align="stretch" p={4} bg="yellow.50" borderRadius="md">
            <Text fontWeight="bold" color="#5b182b">Datos de la licencia de conducir</Text>
            <HStack spacing={4}>
              <FormControl>
                <FormLabel>Tipo de licencia</FormLabel>
                <Select
                  value={formData.tipoLicencia}
                  onChange={(e) => handleInputChange('tipoLicencia', e.target.value)}
                  disabled={isReadOnly}
                >
                  <option value="federal">Federal</option>
                  <option value="estatal">Estatal</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Categoría</FormLabel>
                <Select
                  value={formData.categoriaLicencia}
                  onChange={(e) => handleInputChange('categoriaLicencia', e.target.value)}
                  disabled={isReadOnly}
                >
                  {(formData.tipoLicencia === 'federal' ? LICENCIAS_FEDERALES : LICENCIAS_ESTATALES).map(licencia => (
                    <option key={licencia.codigo} value={licencia.codigo}>
                      {licencia.descripcion}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </HStack>
            <FormControl>
              <FormLabel>Entidad emisora de la licencia</FormLabel>
              <Select
                value={formData.entidadEmisoraLicencia}
                onChange={(e) => handleInputChange('entidadEmisoraLicencia', e.target.value)}
                disabled={isReadOnly}
              >
                {ESTADOS_MEXICO.map(estado => (
                  <option key={estado} value={estado}>{estado}</option>
                ))}
              </Select>
            </FormControl>
          </VStack>
        )}
        
        {formData.tipoGarantia === 'placa' && (
          <VStack spacing={3} align="stretch" p={4} bg="green.50" borderRadius="md">
            <FormControl>
              <HStack>
                <Switch
                  isChecked={formData.usarMismaPlaca}
                  onChange={(e) => {
                    handleInputChange('usarMismaPlaca', e.target.checked);
                    if (e.target.checked) {
                      handleInputChange('placaGarantia', formData.numeroPlaca);
                    }
                  }}
                  isDisabled={isReadOnly}
                />
                <FormLabel mb={0}>Corresponde a la misma placa registrada</FormLabel>
              </HStack>
            </FormControl>
            <FormControl>
              <FormLabel>Placa como garantía</FormLabel>
              <Input
                value={formData.placaGarantia}
                onChange={(e) => handleInputChange('placaGarantia', e.target.value)}
                isReadOnly={isReadOnly || formData.usarMismaPlaca}
                placeholder="Número de placa"
              />
            </FormControl>
          </VStack>
        )}
        
        {formData.tipoGarantia === 'vehiculo' && (
          <VStack spacing={4} align="stretch" p={4} bg="red.50" borderRadius="md">
            <Text fontWeight="bold" color="#5b182b">Aseguramiento del Vehículo</Text>
            <Text fontSize="sm" color="gray.600">
              Al seleccionar vehículo como garantía, se registrará el aseguramiento del vehículo.
            </Text>
            
            <HStack spacing={4}>
              <FormControl>
                <FormLabel>Placa</FormLabel>
                <Input
                  value={formData.numeroPlaca}
                  onChange={(e) => handleInputChange('numeroPlaca', e.target.value)}
                  isReadOnly={isReadOnly}
                  placeholder="Placa del vehículo asegurado"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Estado emisor</FormLabel>
                <Select
                  value={formData.estadoEmisorPlaca}
                  onChange={(e) => handleInputChange('estadoEmisorPlaca', e.target.value)}
                  disabled={isReadOnly}
                >
                  {ESTADOS_MEXICO.map(estado => (
                    <option key={estado} value={estado}>{estado}</option>
                  ))}
                </Select>
              </FormControl>
            </HStack>
            
            <HStack spacing={4}>
              <FormControl>
                <FormLabel>Marca </FormLabel>
                <Input
                  value={formData.marca}
                  onChange={(e) => handleInputChange('marca', e.target.value)}
                  isReadOnly={isReadOnly}
                  placeholder="Marca del vehículo"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Modelo </FormLabel>
                <Input
                  value={formData.modelo}
                  onChange={(e) => handleInputChange('modelo', e.target.value)}
                  isReadOnly={isReadOnly}
                  placeholder="Modelo del vehículo"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Color </FormLabel>
                <Input
                  value={formData.color}
                  onChange={(e) => handleInputChange('color', e.target.value)}
                  isReadOnly={isReadOnly}
                  placeholder="Color del vehículo"
                />
              </FormControl>
            </HStack>
            
            <HStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Número de serie o VIN</FormLabel>
                <Input
                  value={formData.numeroSerieVIN}
                  onChange={(e) => handleInputChange('numeroSerieVIN', e.target.value)}
                  isReadOnly={isReadOnly}
                  placeholder="Número de serie del vehículo"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Número de motor </FormLabel>
                <Input
                  value={formData.numeroMotor}
                  onChange={(e) => handleInputChange('numeroMotor', e.target.value)}
                  isReadOnly={isReadOnly}
                  placeholder="Número de motor"
                />
              </FormControl>
            </HStack>
            
            <FormControl>
              <FormLabel>Tipo de vehículo</FormLabel>
              <Select
                value={formData.tipoVehiculo}
                onChange={(e) => handleInputChange('tipoVehiculo', e.target.value)}
                disabled={isReadOnly}
              >
                {TIPOS_VEHICULO.map(tipo => (
                  <option key={tipo} value={tipo}>{tipo}</option>
                ))}
              </Select>
            </FormControl>
            
            <FormControl>
              <FormLabel>Corralón asignado</FormLabel>
              <Input
                value={formData.corralónAsignado}
                onChange={(e) => handleInputChange('corralónAsignado', e.target.value)}
                isReadOnly={isReadOnly}
                placeholder="Nombre del corralón"
              />
            </FormControl>
            
            <FormControl>
              <FormLabel>Ubicación del aseguramiento</FormLabel>
              <InputGroup>
                <Input
                  value={formData.ubicacionAseguramiento}
                  onChange={(e) => handleInputChange('ubicacionAseguramiento', e.target.value)}
                  isReadOnly={isReadOnly}
                  placeholder="Dirección donde fue remolcado"
                />
                {!isReadOnly && (
                  <InputRightElement width="8rem">
                    <Button 
                      h="1.75rem" 
                      size="sm" 
                      onClick={() => {
                        if (formData.ubicacionAseguramiento) {
                          const geocoder = new window.google.maps.Geocoder();
                          geocoder.geocode({ address: formData.ubicacionAseguramiento }, (results, status) => {
                            if (status === 'OK' && results[0]) {
                              const loc = results[0].geometry.location;
                              setFormData(prev => ({
                                ...prev,
                                latitudAseguramiento: loc.lat(),
                                longitudAseguramiento: loc.lng()
                              }));
                            }
                          });
                        }
                      }}
                    >
                      Buscar
                    </Button>
                  </InputRightElement>
                )}
              </InputGroup>
            </FormControl>
            
            {/* Mapa para ubicación del aseguramiento */}
            <Box>
              <Text fontSize="sm" fontWeight="bold" mb={2}>Ubicación exacta del aseguramiento</Text>
              <Box h="200px" w="100%" borderRadius="md" overflow="hidden">
                <GoogleMap
                  zoom={15}
                  center={{ 
                    lat: Number(formData.latitudAseguramiento) || 19.3158, 
                    lng: Number(formData.longitudAseguramiento) || -98.1667 
                  }}
                  mapContainerStyle={{ width: '100%', height: '100%' }}
                >
                  <Marker
                    position={{ 
                      lat: Number(formData.latitudAseguramiento) || 19.3158, 
                      lng: Number(formData.longitudAseguramiento) || -98.1667 
                    }}
                    draggable={!isReadOnly}
                    onDragEnd={(e) => {
                      if (!isReadOnly) {
                        const lat = e.latLng?.lat();
                        const lng = e.latLng?.lng();
                        
                        if (lat && lng) {
                          const geocoder = new window.google.maps.Geocoder();
                          geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                            if (status === 'OK' && results[0]) {
                              setFormData(prev => ({
                                ...prev,
                                latitudAseguramiento: lat,
                                longitudAseguramiento: lng,
                                ubicacionAseguramiento: results[0].formatted_address
                              }));
                            } else {
                              setFormData(prev => ({ 
                                ...prev, 
                                latitudAseguramiento: lat, 
                                longitudAseguramiento: lng 
                              }));
                            }
                          });
                        }
                      }
                    }}
                  />
                </GoogleMap>
              </Box>
            </Box>
            
            <HStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Fecha del aseguramiento</FormLabel>
                <Input
                  type="date"
                  value={formData.fechaAseguramiento}
                  onChange={(e) => handleInputChange('fechaAseguramiento', e.target.value)}
                  isReadOnly={isReadOnly}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Hora del aseguramiento</FormLabel>
                <Input
                  type="time"
                  value={formData.horaAseguramiento}
                  onChange={(e) => handleInputChange('horaAseguramiento', e.target.value)}
                  isReadOnly={isReadOnly}
                />
              </FormControl>
            </HStack>
            
            <FormControl>
              <FormLabel>Observaciones adicionales</FormLabel>
              <Textarea
                value={formData.observacionesAseguramiento}
                onChange={(e) => handleInputChange('observacionesAseguramiento', e.target.value)}
                isReadOnly={isReadOnly}
                rows={3}
                placeholder="Campo de texto libre sin límite de caracteres"
              />
            </FormControl>
            
            {!isReadOnly && (
              <Box p={3} bg="orange.100" borderRadius="md">
                <Text fontSize="sm" fontWeight="bold" color="orange.800" mb={2}>
                  Documentos de la garantía
                </Text>
                <Text fontSize="xs" color="orange.700">
                  Después de finalizar el registro de la infracción, se habilitará un apartado para adjuntar la orden de grúa en formato PDF.
                </Text>
              </Box>
            )}
          </VStack>
        )}
        
        <Text fontWeight="bold" fontSize="lg" color="#5b182b" mb={4} mt={6}>6. Evidencias</Text>
        
        <EvidenciasFieldSimple
          ref={evidenciasRef}
          onFilesChange={(files) => {
            // Convertir a FileList para mantener compatibilidad
            const dt = new DataTransfer();
            files.forEach(file => dt.items.add(file));
            setEvidenciasFiles(dt.files);
          }}
          isReadOnly={isReadOnly}
          existingFiles={existingEvidencias}
          maxFiles={4}
        />

        <FormControl>
          <FormLabel>Lugar de los Hechos</FormLabel>
          <InputGroup>
            <Input
              value={formData.lugarInfraccion}
              onChange={(e) => handleInputChange('lugarInfraccion', e.target.value)}
              isReadOnly={isReadOnly}
              placeholder="Ingresa la dirección"
            />
            {!isReadOnly && (
              <InputRightElement width="9rem" mr={10}>
                <HStack spacing={1}>                  
                  <Button h="1.75rem" size="sm" onClick={obtenerUbicacionActual}>
                  Ubicación Actual
                  </Button>
                  <Button h="1.75rem" size="sm" onClick={buscarDireccion}>Buscar</Button>                  
                </HStack>
              </InputRightElement>
            )}
          </InputGroup>
        </FormControl>

        <Box h="200px" w="100%">
          <GoogleMap
            zoom={15}
            center={{ lat: Number(formData.latitud) || 19.3158, lng: Number(formData.longitud) || -98.1667 }}
            mapContainerStyle={{ width: '100%', height: '100%' }}
          >
            <Marker
              position={{ lat: Number(formData.latitud) || 19.3158, lng: Number(formData.longitud) || -98.1667 }}
              draggable={!isReadOnly}
              onDragEnd={(e) => {
                if (!isReadOnly) {
                  const lat = e.latLng?.lat();
                  const lng = e.latLng?.lng();
                  
                  if (lat && lng) {
                    const geocoder = new window.google.maps.Geocoder();
                    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                      if (status === 'OK' && results[0]) {
                        setFormData(prev => ({
                          ...prev,
                          latitud: lat,
                          longitud: lng,
                          lugarInfraccion: results[0].formatted_address
                        }));
                      } else {
                        setFormData(prev => ({ ...prev, latitud: lat, longitud: lng }));
                      }
                    });
                  }
                }
              }}
            />
          </GoogleMap>
        </Box>

        <Text fontWeight="bold" fontSize="lg" color="#5b182b" mb={4} mt={6}>Información del oficial</Text>
        
        <HStack spacing={4}>
          <FormControl>
            <FormLabel>Nombre del Oficial</FormLabel>
            <Input
              value={formData.nombreOficial}
              onChange={(e) => handleInputChange('nombreOficial', e.target.value)}
              isReadOnly={isReadOnly}
              placeholder="Nombre completo del oficial"
            />
          </FormControl>
          <FormControl>
            <FormLabel>Grado</FormLabel>
            <Select
              value={formData.gradoOficial}
              onChange={(e) => handleInputChange('gradoOficial', e.target.value)}
              disabled={isReadOnly}
            >
              <option value="Agente">Agente</option>
              <option value="Oficial">Oficial</option>
              <option value="Sargento">Sargento</option>
              <option value="Teniente">Teniente</option>
              <option value="Capitán">Capitán</option>
            </Select>
          </FormControl>
        </HStack>

        <Text fontWeight="bold" fontSize="lg" color="#5b182b" mb={4} mt={6}>Estatus de la infracción</Text>
        
        
        
        <HStack spacing={4}>
          <FormControl>
            <FormLabel>Estatus</FormLabel>
            <Select
              value={formData.estatus}
              onChange={(e) => handleInputChange('estatus', e.target.value)}
              disabled={true}
              bg="gray.100"
            >
              <option value="PENDIENTE">Pendiente</option>
              <option value="PAGADA">Pagada</option>
              <option value="VENCIDA">Vencida</option>
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel>Calificación</FormLabel>
            <Select
              value={formData.calificado ? 'true' : 'false'}
              onChange={(e) => handleInputChange('calificado', e.target.value === 'true')}
              disabled={true}
              bg="gray.100"
            >
              <option value="false">Por calificar</option>
              <option value="true">Calificada</option>
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel>Monto</FormLabel>
            <NumberInput
              value={formData.monto}
              onChange={(_, value) => {
                const numValue = isNaN(value) ? 0 : Math.max(0, value);
                handleInputChange('monto', numValue);
              }}
              isReadOnly={isReadOnly}
              min={0}
              precision={2}
              step={0.01}
            >
              <NumberInputField />
            </NumberInput>
          </FormControl>
        </HStack>
        


        {/* Sección de orden de grúa - se muestra después de guardar multa con vehículo como garantía */}
        {(showOrdenGruaUpload || (data && formData.tipoGarantia === 'vehiculo')) && (
          <VStack spacing={4} align="stretch" p={4} bg="purple.50" borderRadius="md" mt={6}>
            <Text fontWeight="bold" color="#5b182b">Documentos de la Garantía - Orden de Grúa</Text>
            <Text fontSize="sm" color="gray.600">
              Adjunta la orden de grúa en formato PDF asociada a esta infracción.
            </Text>
            
            <FormControl>
              <FormLabel>Orden de Grúa (PDF)</FormLabel>
              <Input
                type="file"
                accept=".pdf"
                disabled={isReadOnly}
                onChange={(e) => setOrdenGruaFiles(e.target.files)}
                sx={{
                  '::file-selector-button': {
                    height: 10,
                    padding: 0,
                    mr: 4,
                    background: 'none',
                    border: 'none',
                    fontWeight: 'bold',
                    color: '#5b182b',
                    cursor: 'pointer'
                  }
                }}
              />
              <Text fontSize="xs" color="gray.500" mt={1}>
                Formato: PDF únicamente
              </Text>
              {ordenGruaFiles && (
                <Text fontSize="xs" color="blue.500" mt={1}>
                  {ordenGruaFiles.length} archivo(s) seleccionado(s)
                </Text>
              )}
            </FormControl>
            
            {!isReadOnly && ordenGruaFiles && (
              <Button
                size="sm"
                bg="#9333ea"
                color="white"
                _hover={{ bg: "#7c3aed" }}
                onClick={async () => {
                  if (formData.id && ordenGruaFiles) {
                    try {
                      const formDataOrden = new FormData();
                      Array.from(ordenGruaFiles).forEach(file => {
                        formDataOrden.append('ordenGrua', file);
                      });
                      
                      const response = await fetch(`${API_BASE_URL}/api/multas-transito/${formData.id}/orden-grua`, {
                        method: 'POST',
                        body: formDataOrden
                      });
                      
                      if (response.ok) {
                        toast({
                          title: 'Éxito',
                          description: 'Orden de grúa subida correctamente',
                          status: 'success',
                          duration: 5000,
                        });
                        setOrdenGruaFiles(null);
                      } else {
                        throw new Error('Error al subir orden de grúa');
                      }
                    } catch (error) {
                      toast({
                        title: 'Error',
                        description: 'No se pudo subir la orden de grúa',
                        status: 'error',
                        duration: 5000,
                      });
                    }
                  }
                }}
              >
                Subir Orden de Grúa
              </Button>
            )}
            
            <Box p={3} bg="yellow.100" borderRadius="md">
              <Text fontSize="sm" fontWeight="bold" color="yellow.800" mb={1}>
                Estatus de la infracción: Pendiente - Vehículo asegurado
              </Text>
              <Text fontSize="xs" color="yellow.700">
                El vehículo ha sido marcado como garantía asegurada. Los datos del aseguramiento están registrados en el detalle de la infracción.
              </Text>
            </Box>
          </VStack>
        )}
        
        {modo !== 'view' && (
          <HStack spacing={4}>
            <Button 
              bg="#691f30" 
              color="white" 
              _hover={{ bg: "#5b182b" }} 
              onClick={handleRegistrar}
              isLoading={uploadingFiles}
              loadingText={uploadingFiles ? "Subiendo archivos..." : "Guardando..."}
              disabled={uploadingFiles}
            >
              {modo === 'new' ? 'Registrar Infracción' : 'Actualizar Infracción'}
            </Button>
            <Button bg="#bb8c52" color="white" _hover={{ bg: "#a67a47" }} onClick={handleImprimir}>
              Imprimir Infracción
            </Button>
          </HStack>
        )}
      </VStack>
      
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalBody p={0}>
            <Image src={selectedImage} w="100%" h="auto" />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
});

export default RegistroMultaPolicia;
